import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";

import { TarifReducedDto } from "@/types/api/api-types";
import { DataTableColumnHeader } from "@/components/ui/customs/data-table/data-table-column-header";
import { PencilIcon, Trash, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDate } from "date-fns";

interface TarifsTableColumnsProps {
  setUpdateTarif: (tarif: TarifReducedDto) => void;
  setDeleteTarif: (tarif: TarifReducedDto) => void;
  setDetailCompaniesTarif: (tarif: TarifReducedDto) => void;
}

export const useTarifsTableColumns = ({
  setUpdateTarif,
  setDeleteTarif,
  setDetailCompaniesTarif,
}: TarifsTableColumnsProps): ColumnDef<TarifReducedDto>[] => {
  const { t } = useTranslation("boParameters");

  return [
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("tarifsTable.columns.label")} />
      ),
      cell: ({ row }) => {
        return (
          <p className="overflow-hidden text-ellipsis text-gray-500">{row.getValue("label")}</p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "following",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("tarifsTable.columns.following")} />
      ),
      cell: ({ row }) => {
        return (
          <p className="w-[80px] text-gray-500">
            {row.original?.following?.label
              ? t("tarifsTable.followingValue", {
                  label: row.original.following.label,
                  percentage: row.original.changePercentage,
                })
              : t("tarifsTable.notApplicable")}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "_count",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("tarifsTable.columns.companiesCount")} />
      ),
      cell: ({ row }) => {
        return <p className="w-[80px] text-gray-500">{row.original?._count?.companies}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("tarifsTable.columns.createdAt")} />
      ),
      cell: ({ row }) => {
        return (
          <p className="w-[80px] text-gray-500">
            {formatDate(row.getValue("createdAt"), "dd/MM/yyyy")}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "updatedAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("tarifsTable.columns.updatedAt")} />
      ),
      cell: ({ row }) => {
        return (
          <p className="w-[80px] text-gray-500">
            {formatDate(row.getValue("updatedAt"), "dd/MM/yyyy")}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "actions",
      header: ({ column }) => <DataTableColumnHeader column={column} title="" />,
      maxSize: 120,
      size: 120,
      cell: ({ row }) => {
        return (
          <div className="flex w-full justify-end text-gray-500">
            <Button
              variant="ghost"
              className="text-blue-500 hover:text-blue-600"
              size="icon"
              onClick={() => setUpdateTarif(row.original)}
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDetailCompaniesTarif(row.original)}
            >
              <Building2 className="h-4 w-4" />
            </Button>
            {!row.original.isDefault && (
              <Button
                variant="ghost"
                className="text-red-500 hover:text-red-600"
                size="icon"
                onClick={() => setDeleteTarif(row.original)}
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
  ];
};
